/**
 * Practitioner notes — HTTP client (GET/POST/DELETE).
 */
import { apiFetch, pollEndpoint } from '@/lib/api-client';
import type { ClientPractitionerNote } from '@/lib/clients/activity-timeline';
import {
  MAX_PRACTITIONER_NOTE_LENGTH,
  type PractitionerNoteRecord,
} from '@/lib/clients/practitioner-notes';

export { MAX_PRACTITIONER_NOTE_LENGTH };

interface CreateNoteResponse {
  success: boolean;
  noteId: string;
}

interface DeleteNoteResponse {
  success: boolean;
}

function toClientNote(record: PractitionerNoteRecord): ClientPractitionerNote {
  return {
    id: record.id,
    text: record.text,
    createdByUid: record.createdByUid,
    createdByEmail: record.createdByEmail,
    createdByName: record.createdByName,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  };
}

export async function listPractitionerNotes(clientId: string): Promise<ClientPractitionerNote[]> {
  const rows = await apiFetch<PractitionerNoteRecord[]>(
    `/clients/${clientId}/practitioner-notes`
  );
  return rows.map(toClientNote);
}

export async function createPractitionerNote(
  clientId: string,
  text: string
): Promise<string> {
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error('Cannot save an empty note');
  }
  if (trimmed.length > MAX_PRACTITIONER_NOTE_LENGTH) {
    throw new Error(
      `Note is too long (${trimmed.length} characters). Maximum is ${MAX_PRACTITIONER_NOTE_LENGTH}.`
    );
  }

  const response = await apiFetch<CreateNoteResponse>(
    `/clients/${clientId}/practitioner-notes`,
    {
      method: 'POST',
      body: JSON.stringify({ text: trimmed }),
    }
  );
  return response.noteId;
}

export async function deletePractitionerNote(
  clientId: string,
  noteId: string
): Promise<void> {
  await apiFetch<DeleteNoteResponse>(
    `/clients/${clientId}/practitioner-notes/${noteId}`,
    { method: 'DELETE' }
  );
}

export function subscribeToPractitionerNotes(
  clientId: string,
  callback: (notes: ClientPractitionerNote[]) => void,
  intervalMs: number = 5000,
  onError?: (error: Error) => void
): () => void {
  return pollEndpoint<PractitionerNoteRecord[]>(
    `/clients/${clientId}/practitioner-notes`,
    (rows) => callback(rows.map(toClientNote)),
    intervalMs,
    onError
  );
}
